'use client';

import { useRef } from 'react';

import { Box, Grid, Typography } from '@mui/material';
import cx from 'clsx';
import Image from 'next/image';

import colors from '@/styles/themes/colors';
import { Solution, SolutionColor } from '@/types/solution.type';
import useIntersectionObserver from '@/utils/hooks/useIntersectionObserver';

import styles from './SolutionItem.module.scss';

interface SolutionItemProps {
  solution: Solution;
  color?: SolutionColor;
}

const SolutionItem = ({ solution, color = 'blue' }: SolutionItemProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useIntersectionObserver(ref);
  const colorClass = styles[`color${color.charAt(0).toUpperCase()}${color.slice(1)}`];

  return (
    <Box ref={ref} className={cx(styles.solutionItem, colorClass, { [styles.visible]: isVisible })}>
      <Box className={styles.imageContainer}>
        <Image
          fill
          src={solution.image.src}
          alt={solution.image.alt}
          className={styles.image}
          sizes="(min-width: 1024px) 684px, 100vw"
        />
      </Box>
      <Box>
        <Typography variant="subtitle1" component="h3" color={colors.white} fontWeight={500} marginBottom={2}>
          {solution.title}
        </Typography>
        <Grid container component="ul" spacing={1} columns={2} className={styles.featuresList}>
          {solution.features.map(feature => (
            <Grid size={{ xs: 2, sm: 1 }} component="li" key={feature} className={styles.featureItem}>
              <Typography variant="body1" color={colors.graytone200}>
                {feature}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default SolutionItem;
